<template>
  <div 
    class="fts-login-form-element fts-form-group"
    :class="{'with-error': error}"
  >
    <!-- username -->
    <template v-if="type === 'username'">
      <div class="login-form-element-group">
        <div class="form-element-prepend">
          <font-awesome-icon :icon="['far', 'envelope']" fixed-width />
        </div>

        <input
          type="text"
          class="fts-form-control"
          :placeholder="placeholder"
          :autocomplete="`username-disabled-${uid}`"
          :value="value"
          disabled="disabled"
          v-if="disabled"
        >

        <template v-else>
          <input 
            type="text" 
            class="fts-form-control" 
            :placeholder="placeholder || 'Email'" 
            :autocomplete="`username-${uid}`" 
            v-model="mutableValue" 
            ref="username" 
            @input="onInput" 
            @blur="onBlur" 
          />
        </template>
      </div>

      <div class="fts-form-error" v-if="error">
        {{error}}
      </div>
    </template>
    <!-- password -->
    <template v-if="type === 'password'">
      <div class="login-form-element-group">
        <div class="form-element-prepend">
          <font-awesome-icon :icon="['fas', 'lock']" fixed-width />
        </div>

        <input 
          :type="isPasswordReveal ? 'text' : 'password'" 
          class="fts-form-control" 
          :placeholder="placeholder || 'Password'" 
          :autocomplete="`password-${uid}`" 
          v-model="mutableValue" 
          ref="password" 
          @input="onInput" 
          @blur="onBlur" 
        />

        <div class="form-element-append">
          <font-awesome-icon
            :icon="['far', 'eye-slash']"
            fixed-width
            @click="isPasswordReveal=false"
            v-if="isPasswordReveal"
          />
          <font-awesome-icon
            :icon="['far', 'eye']"
            fixed-width
            @click="isPasswordReveal=true"
            v-else
          />
        </div>
      </div>

      <div
        class="fts-form-helper"
        v-if="
          passwordHint &&
          !(
            validator &&
            validator.$error &&
            validator.required
          )
        "
      >
        Password must contain 1 alphabet, 1 numeric, and at least 12
        characters.
      </div>

      <div class="fts-form-error" v-if="error">
        {{error}}
      </div>
    </template>
    <!-- display name -->
    <template v-else-if="type === 'display-name'">
      <div class="login-form-element-group">
        <div class="form-element-prepend">
          <font-awesome-icon :icon="['far', 'user']" fixed-width />
        </div>

        <input 
          type="text" 
          class="fts-form-control" 
          :placeholder="placeholder || 'Your display name, eg. John Smith'" 
          :autocomplete="`display-name-${uid}`" 
          v-model="mutableValue" 
          ref="displayName" 
          @input="onInput" 
          @blur="onBlur" 
        />
      </div>

      <div class="fts-form-error" v-if="error">
        {{error}}
      </div>
    </template>
    <!-- workspace name -->
    <template v-else-if="type === 'workspace-name'">
      <div class="login-form-element-group">
        <div class="form-element-prepend">
          <font-awesome-icon :icon="['far', 'building']" fixed-width />
        </div>

        <input 
          type="text" 
          class="fts-form-control" 
          :placeholder="placeholder || 'Company name, eg. Acme Corporation'" 
          :autocomplete="`workspace-name-${uid}`" 
          v-model="mutableValue" 
          ref="workspaceName" 
          @input="onInput" 
          @blur="onBlur" 
        >
      </div>

      <div 
        class="fts-form-error"
        v-if="error && validator.required === false"
      >{{error}}</div>

      <div
        class="workspace-name-hint"
        v-else-if="
          validator &&
          validator.$error &&
          validator.format === false
        "
      >
        <div class="workspace-name-hint-wrapper">Allow alphabet and numeric only.</div>
        <div class="workspace-name-hint-wrapper" v-if="workspaceNameSpecialCharacters">
          Allow special characters
          <span
            class="workspace-name-hint-highlight"
            v-for="(x, index) in workspaceNameSpecialCharacters"
            :key="`workspace-special-character-${index}`"
          >
            {{x}}
          </span>
        </div>
      </div>
    </template>
    <!-- code -->
    <template v-else-if="type === 'code'">
      <div class="login-form-element-group">
        <input 
          type="text" 
          name="code" 
          class="fts-form-control" 
          :placeholder="placeholder || 'Code'" 
          autocomplete="off" 
          v-model="mutableValue" 
          ref="code" 
          @input="onInput" 
          @blur="onBlur" 
        />
      </div>

      <div class="fts-form-error" v-if="error">
        {{error}}
      </div>
    </template>
  </div>
</template>

<script>
import designValidator from '../commons/designValidator';
export default {
  props: {
    value: {
      required: true
    },
    type: {
      type: String,
      validator: value => [
        'username',
        'password',
        'display-name',
        'workspace-name',
        'code'
      ].includes(value)
    },
    displayName: String,
    validator: Object,
    disabled: Boolean,
    placeholder: String,
    passwordHint: Boolean
  },
  model: {
    prop: 'value',
    event: 'updateValue'
  },
  data: () => ({
    uid: null,
    workspaceNameSpecialCharacters: Object.freeze(designValidator.workspaceNameSpecialCharacters.split("")),
    isPasswordReveal: false
  }),
  methods: {
    focus() {
      const refName = {
        'username': 'username',
        'password': 'password',
        'display-name': 'displayName',
        'workspace-name': 'workspaceName',
        'code': 'code',
      }[this.type];

      this.$refs[refName]?.focus?.();
    },
    onInput() {
      if (this.validator && this.validator.required === false) {
        this.validator.$touch();
      }
    },
    onBlur() {
      this.validator && this.validator.$touch();
    }
  },
  computed: {
    mutableValue: {
      get(){
        return this.value;
      },
      set(val) {
        this.$emit("updateValue", val);
      }
    },
    error() {
      if (!this.disabled && this.validator && this.validator.$error) {
        if (this.validator.email === false) {
          return "Please enter email address in format: example@mail.com";
        } else if (this.validator.required && this.validator.password === false) {
          return `${
              this.displayName ? this.displayName : "Password"
            } must contain 1 alphabet, 1 numeric, and at least 12 characters.`;
        } else if (this.validator.required && this.validator.sameAs === false) {
          return `${this.displayName ? this.displayName : "Field"} must be identical.`;
        } else if (this.validator.format === false) {
          return `${this.displayName ? this.displayName : "Field"} cannot contain invalid characters.`;
        } else {
          return `${this.displayName ? this.displayName : "Field"} is required.`; 
        }
      }
    }
  },
  mounted() {
    this.uid = this._uid;
  }
}
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-login-form-element {
  .login-form-element-group {
    position: relative;
    .fts-form-control:not(:first-child) {
      padding-left: 2.5rem;
    }
    .fts-form-control:not(:last-child) {
      padding-right: 2.5rem;
    }
  }
  .form-element-prepend {
    @include absolute-center-y;
    left: 0.75rem;
    justify-content: center;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
    color: var(--fts-default);
    opacity: 0.75;
  }
  .form-element-append {
    @include absolute-center-y;
    right: 0.75rem;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
    color: var(--fts-default);
    opacity: 0.75;
    > svg {
      cursor: pointer;
    }
    &:hover {
      color: var(--fts-text);
      opacity: 1;
    }
  }
  /* workspace name hint */
  .workspace-name-hint {
    margin-top: .575rem;;
    .workspace-name-hint-wrapper {
      font-size: .75rem;
      font-weight: 300;
      color: var(--fts-default);
      line-height: 1.25;
      &:not(:first-child) {
        margin-top: .375rem;
      }
      .workspace-name-hint-highlight {
        margin-left: .275rem;
        font-weight: 500;
      }
    }
  }
  &:focus-within {
    .form-element-prepend {
      color: var(--fts-text);
    }
  }
  &.with-error {
    .form-element-prepend {
      color: var(--fts-danger);
      opacity: 1;
    }
    .fts-form-control {
      @include theme {
        box-shadow: inset 0 1px 1px rgba(theme-get('fts-danger'), 0.075), 0 0 4px rgba(theme-get('fts-danger'), 0.5);
      }
      border-color: var(--fts-danger);
    }
  }
}
</style>